import { MouseEvent, useCallback, useContext, useMemo, useRef } from 'react'

import { Heading, theme } from '@hhgtech/hhg-components'
import { DataTrackingTypeHTMLElement } from '@hhgtech/hhg-components/types'
import {
  AspectRatio,
  Box,
  BoxProps,
  Stack,
  rem,
  useMantineTheme,
} from '@mantine/core'

import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import LeadgenWithBanner, {
  LeadgenWithBannerHandlerProps,
} from 'components/organisms/serviceConnections/leadgenWithBanner'
import { CarePathDefaultContext } from 'components/templates/carePath/landing/context'
import { useGetEncodedGaId } from 'hooks/useGetClientGaId'
import { useHandlerUrls } from 'hooks/useHandlerUrls'
import { useLeavePageConfirm } from 'hooks/useLeavePageConfirm'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ListBanner, SpotlightLeadgenSession } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { trimVietnameseAccent } from 'utils/trimVietnameseAccent'
import { formatUrlWithEncrypedGa } from 'utils/url'

import useStyles from './index.styles'

const MAPPED_COLOR = {
  white: '#ffffff',
  'yellow-600': theme.colors.yellow600,
  'violet-600': theme.colors.violet600,
  'green-600': theme.colors.green600,
  primary: theme.colors.primaryBase,
  none: 'transparent',
}
export type BannerProps = {
  banner: ListBanner
  hasLeavePageConfirm?: boolean
  bgSessionColor?: string
  ratio?: number
  keepNatureRatio?: boolean
  requireLogin?: boolean
  openChatWoot?: boolean
  onItemClick?: (v: string) => void
} & BoxProps &
  DataTrackingTypeHTMLElement

const Banner = ({
  banner: bannerProp,
  bgSessionColor,
  hasLeavePageConfirm,
  ratio = 336 / 356,
  keepNatureRatio,
  requireLogin,
  openChatWoot,
  onItemClick,
  className,
  classNames,
  styles,
  ...props
}: BannerProps) => {
  const { asPath } = useRouterExtend()
  const { detectPage, keyID, activeTheme } = useContext(CarePathDefaultContext)
  const { isCarePath } = detectPage
  const {
    url = '',
    title = '',
    banner,
    expired_day_status,
    card_color,
  } = bannerProp
  const bannerColor = MAPPED_COLOR[card_color as keyof typeof MAPPED_COLOR]
  const { classes, cx } = useStyles(
    { bannerColor, bgSessionColor },
    {
      name: 'ClickableBannerItem',
      styles,
      classNames,
    },
  )

  const { handleClick } = useLeavePageConfirm(url)
  const mantineTheme = useMantineTheme()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { encodedGaId } = useGetEncodedGaId()

  const linkData = useHandlerUrls([url])
  const { isExternalUrl } = linkData?.[0] || {}
  const leadgenRef = useRef<LeadgenWithBannerHandlerProps>(null)
  const finalUrl = isCarePath ? formatUrlWithEncrypedGa(url, encodedGaId) : url
  const detectCarepathVn = useMemo(() => keyID === 'carepathvn', [keyID])
  const trimTitle = useMemo(
    () => trimVietnameseAccent(title.toLowerCase().replace(/\s/g, '')),
    [],
  )
  const updateParam = useCallback((action: 'add' | 'remove', name: string) => {
    const searchParams = new URLSearchParams(window.location.search)
    action === 'add'
      ? searchParams.set(name, 'true')
      : searchParams.delete(name)
    const newParams = searchParams.toString()
    window.history.replaceState(
      {},
      '',
      `${asPath.split('?')[0]}${
        newParams === '' ? '' : '?'
      }${searchParams.toString()}`,
    )
  }, [])

  if (!banner) return null

  return (
    <>
      <Box
        component={expired_day_status || openChatWoot ? 'div' : 'a'}
        href={finalUrl}
        target={isExternalUrl ? '_blank' : '_self'}
        className={cx('pointer-event-child-none', classes.root, className)}
        data-event-label={finalUrl}
        {...props}
        onClick={(e: MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
          if (Object.keys(bannerProp.leadgencode || {}).length > 0) {
            e.preventDefault()
            detectCarepathVn && updateParam('add', trimTitle)
            leadgenRef.current?.openModal()
          } else if (!isExternalUrl && url.includes('#')) {
            e.preventDefault()
            const splitUrl = url.split('#')
            onItemClick?.(splitUrl[splitUrl.length - 1])
          } else {
            if (requireLogin && !isLoggedIn) {
              e.preventDefault()
              redirectToSSOWithReturnUrl(window.location.href)
            } else if (hasLeavePageConfirm) {
              handleClick(e)
            } else {
              if (openChatWoot && window?.$chatwoot) {
                e.preventDefault()
                window?.$chatwoot.toggle('open')
              }
            }
          }
        }}
      >
        <div className={classes.banner}>
          <Stack
            ta="center"
            justify="space-between"
            sx={(theme) => ({
              height: '100%',
              gap: rem(8),
              [theme.fn.largerThan('sm')]: {
                gap: rem(16),
              },
            })}
          >
            <div className={classes.title}>
              <Heading
                tag="h5"
                color={
                  ['white', 'none'].includes(card_color)
                    ? mantineTheme.colors.gray[8]
                    : '#ffffff'
                }
              >
                {title}
              </Heading>
            </div>
            {keepNatureRatio ? (
              <img src={banner} alt="" loading="lazy" />
            ) : (
              <AspectRatio
                className={classes.bannerIframe}
                ratio={ratio}
                sx={{
                  borderRadius: 8,
                  overflow: 'hidden',
                  ...(expired_day_status && {
                    filter: 'grayscale(1)',
                  }),
                }}
              >
                <img src={banner} alt="" loading="lazy" />
              </AspectRatio>
            )}
          </Stack>
        </div>
      </Box>
      {Object.keys(bannerProp.leadgencode || {}).length > 0 && (
        <LeadgenWithBanner
          isModal
          desktopModalType="fullscreen"
          ref={leadgenRef}
          data={
            {
              isSkippable: true,
              campaignCode: bannerProp.leadgencode?.code,
              banner: {
                desktop: bannerProp.leadgencode?.image_desktop,
                mobile: bannerProp.leadgencode?.image,
              },
              thankYouMessage: (() => {
                const listBlockThankyou = JSON.parse(
                  bannerProp.leadgencode?.extra_fields?.listBlockThankyou ||
                    null,
                )
                return {
                  title:
                    listBlockThankyou?.find((i: any) => i.name == 'TitleBlock')
                      ?.data?.value || '',
                  description:
                    listBlockThankyou?.find(
                      (i: any) => i.name == 'SubtitleBlock',
                    )?.data?.value || '',
                }
              })(),
              campaigns: bannerProp.leadgencode,
            } as SpotlightLeadgenSession
          }
          primaryColor={activeTheme?.primary}
          // styles={leadgenStyles}
          onCloseForm={() => {
            url && window.open(url)
          }}
          onDone={() => {
            detectCarepathVn && updateParam('remove', trimTitle)
          }}
        />
      )}
    </>
  )
}

export { Banner }
